import { useCallback, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import { useDeskId } from '@/hooks/useDeskId';
import useUser from '@/hooks/useUser';
import { useViews } from '@/hooks/useViews';
import { useDepartments } from '@/modules/departments/hooks/useDepartments';
import { selectAgentNameById } from '@/redux/presence/selectors';
import { RootState } from '@/shared/types/state';

import { setView } from '../redux/actions';
import {
  selectViewSelectedAgent,
  selectViewSelectedDepartment,
  selectViewSelectedStatus,
  selectViewSelectedBrain,
  selectViewSelectedView,
  selectViewTitle,
} from '../redux/selectors';

export const useMessageList = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [urlSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const deskId = useDeskId();
  const { departments } = useDepartments(deskId);
  const agentIdFromParams = urlSearchParams.get('agent_id');
  const brainIdFromParams = urlSearchParams.get('brain_id');
  const departmentIdFromParams = urlSearchParams.get('department_id');
  const viewIdFromParams = urlSearchParams.get('view_id');
  const category = urlSearchParams.get('category');

  const selectedAgent = useSelector(selectViewSelectedAgent);
  const selectedDepartment = useSelector(selectViewSelectedDepartment);
  const selectedStatus = useSelector(selectViewSelectedStatus);
  const selectedBrain = useSelector(selectViewSelectedBrain);
  const selectedView = useSelector(selectViewSelectedView);
  const selectedTitle = useSelector(selectViewTitle);

  const { view } = useViews(selectedView);

  useEffect(() => {
    const defaultView = {
      selectedAgent: '',
      selectedBrain: '',
      selectedStatus: 'open',
      title: '',
      selectedDepartment: '',
      selectedView: '',
    };

    const handleDispatch = (overrides) => {
      dispatch(setView({ ...defaultView, ...overrides }));
    };

    switch (category) {
      case 'me':
        handleDispatch({ selectedAgent: user?.user_id, title: category });
        break;

      case 'unassigned':
        handleDispatch({ selectedAgent: 'null', selectedBrain: 'null', title: category });
        break;

      case 'all':
        handleDispatch({ title: category });
        break;

      case 'spam':
      case 'trash':
        handleDispatch({ selectedStatus: category, title: category });
        break;

      default:
        break;
    }

    if (brainIdFromParams) {
      handleDispatch({ selectedBrain: 'brain' });
    }

    if (agentIdFromParams) {
      handleDispatch({ selectedAgent: agentIdFromParams });
    }

    if (departmentIdFromParams) {
      handleDispatch({ selectedDepartment: departmentIdFromParams });
    }
    if (viewIdFromParams) {
      handleDispatch({
        selectedView: viewIdFromParams,
        // Override default 'open' status
        selectedStatus: '',
      });
    }
  }, [
    agentIdFromParams,
    departmentIdFromParams,
    dispatch,
    category,
    user?.user_id,
    brainIdFromParams,
    t,
    viewIdFromParams,
  ]);

  const getTitleFromSelectedTitle = useCallback(() => {
    return selectedTitle ? t(`conversation.${selectedTitle}`) : null;
  }, [selectedTitle, t]);

  const selectedAgentName = useSelector((state: RootState) =>
    selectAgentNameById(state, selectedAgent)
  );

  const getTitleFromSelectedAgent = useCallback(() => {
    return selectedAgent ? selectedAgentName : null;
  }, [selectedAgent, selectedAgentName]);

  const getTitleFromSelectedBrain = useCallback(() => {
    return selectedBrain ? t('conversation.assistant') : null;
  }, [selectedBrain, t]);

  const getTitleFromSelectedDepartment = useCallback(() => {
    return selectedDepartment
      ? departments?.find((department) => department.department_id === selectedDepartment)?.name
      : null;
  }, [selectedDepartment, departments]);

  const getTitleFromSelectedView = useCallback(() => {
    return view?.name;
  }, [view]);

  const title = useMemo(() => {
    return (
      getTitleFromSelectedTitle() ||
      getTitleFromSelectedAgent() ||
      getTitleFromSelectedBrain() ||
      getTitleFromSelectedDepartment() ||
      getTitleFromSelectedView() ||
      selectedStatus
    );
  }, [
    getTitleFromSelectedTitle,
    getTitleFromSelectedAgent,
    getTitleFromSelectedBrain,
    getTitleFromSelectedDepartment,
    getTitleFromSelectedView,
    selectedStatus,
  ]);

  return { title };
};
